<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Clock, Plus, Trash2, RefreshCw, Layers } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Interfaces
interface TimelineEvent {
  label: string
}

interface TimelinePeriod {
  period: string
  events: TimelineEvent[]
}

interface TimelineSection {
  name: string
  periods: TimelinePeriod[]
}

// Estado visual
const timelineTitle = ref('Evolución del Proyecto')
const sections = ref<TimelineSection[]>([
  {
    name: 'Fase Inicial',
    periods: [
      {
        period: 'Enero 2023',
        events: [{ label: 'Idea original' }, { label: 'Prototipo' }]
      }
    ]
  }
])

// Acciones
const addSection = () => {
  sections.value.push({
    name: `Nueva Sección ${sections.value.length + 1}`,
    periods: []
  })
}

const removeSection = (secIdx: number) => {
  sections.value.splice(secIdx, 1)
}

const addPeriod = (secIdx: number) => {
  sections.value[secIdx].periods.push({
    period: 'Nuevo Periodo',
    events: [{ label: 'Evento' }]
  })
}

const removePeriod = (secIdx: number, perIdx: number) => {
  sections.value[secIdx].periods.splice(perIdx, 1)
}

const addEvent = (secIdx: number, perIdx: number) => {
  sections.value[secIdx].periods[perIdx].events.push({ label: 'Nuevo Evento' })
}

const removeEvent = (secIdx: number, perIdx: number, evIdx: number) => {
  sections.value[secIdx].periods[perIdx].events.splice(evIdx, 1)
}

// Generar código desde el estado visual
const generateMermaid = () => {
  let code = `timeline\n`
  if (timelineTitle.value.trim()) {
    code += `    title ${timelineTitle.value.trim()}\n`
  }
  
  sections.value.forEach(section => {
    if (section.name.trim()) {
      code += `    section ${section.name.trim()}\n`
    }
    
    section.periods.forEach(period => {
      const p = period.period.trim() || 'Periodo'
      
      if (period.events.length > 0) {
        code += `        ${p}`
        period.events.forEach(event => {
            const evLabel = event.label.trim().replace(/:/g, '-') || 'Evento'
            code += ` : ${evLabel}`
        })
        code += '\n'
      } else {
        code += `        ${p}\n`
      }
    })
  })
  
  editableCode.value = code
}

// Sincronizar desde código a estado visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newSections: TimelineSection[] = []
  let currentSection: TimelineSection = { name: '', periods: [] }
  let newTitle = ''
  
  for (const line of lines) {
    const tLine = line.trim()
    if (tLine.startsWith('timeline')) {
      continue
    } else if (tLine.startsWith('title ')) {
      newTitle = tLine.replace('title ', '').trim()
    } else if (tLine.startsWith('section ')) {
      if (currentSection.name || currentSection.periods.length > 0) {
        newSections.push(currentSection)
      }
      currentSection = { name: tLine.replace('section ', '').trim(), periods: [] }
    } else if (tLine.includes(':')) {
      const parts = tLine.split(':').map(s => s.trim())
      
      if (parts[0] === '' && currentSection.periods.length > 0) {
        // Evento que continúa del periodo anterior
        const lastPeriod = currentSection.periods[currentSection.periods.length - 1]
        for (let i = 1; i < parts.length; i++) {
          if (parts[i]) {
            lastPeriod.events.push({ label: parts[i] })
          }
        }
      } else if (parts[0] !== '') {
        // Nuevo periodo
        const p: TimelinePeriod = { period: parts[0], events: [] }
        for (let i = 1; i < parts.length; i++) {
          if (parts[i]) {
            p.events.push({ label: parts[i] })
          }
        }
        currentSection.periods.push(p)
      }
    }
  }
  
  if (currentSection.name || currentSection.periods.length > 0) {
    newSections.push(currentSection)
  }
  
  timelineTitle.value = newTitle
  if (newSections.length > 0) {
    sections.value = newSections
  }
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([timelineTitle, sections], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Línea de Tiempo (Timeline)"
    description="Crea líneas de tiempo históricas o proyecciones a futuro. Agrupa periodos y eventos en secciones."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="saveAndClose"
  >
    <template #icon>
      <Clock class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" @click="addSection"><Plus class="w-4 h-4 mr-2" /> Añadir Sección</Button>
    </template>

    <template #canvas>
      <div class="h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6">
        
        <!-- Título -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-2">
          <label class="text-sm font-semibold">Título Principal</label>
          <input 
            v-model="timelineTitle" 
            class="border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary font-medium"
            placeholder="Ej. Historia de la Inteligencia Artificial"
          />
        </div>

        <!-- Secciones -->
        <div class="flex flex-col gap-6">
          <div 
            v-for="(section, secIdx) in sections" 
            :key="secIdx"
            class="bg-card border rounded-lg shadow-sm flex flex-col overflow-hidden"
          >
            <!-- Cabecera de la Sección -->
            <div class="bg-muted/40 border-b p-3 flex items-center justify-between gap-4">
              <div class="flex-1 flex items-center gap-2">
                <Layers class="w-4 h-4 text-muted-foreground" />
                <input 
                  v-model="section.name" 
                  class="flex-1 bg-transparent text-sm font-bold focus:outline-none focus:border-b border-primary/50"
                  placeholder="Nombre de la Sección (Opcional)"
                />
              </div>
              <button 
                @click="removeSection(secIdx)" 
                class="text-muted-foreground hover:text-destructive transition-colors p-1"
                title="Eliminar Sección completa"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>

            <!-- Contenido de la Sección (Periodos) -->
            <div class="p-4 flex flex-col gap-4">
              <div v-if="section.periods.length === 0" class="text-center text-muted-foreground py-4 text-xs border border-dashed rounded">
                Sin periodos en esta sección.
              </div>

              <!-- Lista de Periodos -->
              <div 
                v-for="(period, perIdx) in section.periods" 
                :key="perIdx"
                class="flex flex-col gap-2 relative pl-4 border-l-2 border-primary/30"
              >
                <!-- Bolita del timeline visual -->
                <div class="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center shadow-sm">
                  <div class="w-2 h-2 rounded-full bg-background"></div>
                </div>

                <div class="flex items-center gap-2">
                  <input 
                    v-model="period.period" 
                    class="font-bold text-sm bg-background border rounded px-2 py-1 w-32 focus:outline-none focus:ring-1 focus:ring-primary shadow-sm"
                    placeholder="Ej. 2024"
                  />
                  <button 
                    @click="removePeriod(secIdx, perIdx)" 
                    class="text-muted-foreground hover:text-destructive opacity-50 hover:opacity-100 transition-opacity p-1"
                  >
                    <Trash2 class="w-3 h-3" />
                  </button>
                </div>

                <!-- Eventos dentro del Periodo -->
                <div class="flex flex-wrap gap-2 mt-1 ml-2">
                  <div 
                    v-for="(event, evIdx) in period.events" 
                    :key="evIdx"
                    class="flex items-center bg-muted border rounded-full px-3 py-1 shadow-sm group"
                  >
                    <input 
                      v-model="event.label" 
                      class="bg-transparent text-xs focus:outline-none text-foreground font-medium w-auto min-w-[60px]"
                      placeholder="Nombre del evento"
                    />
                    <button 
                      @click="removeEvent(secIdx, perIdx, evIdx)" 
                      class="ml-2 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"
                    >
                      <Trash2 class="w-3 h-3" />
                    </button>
                  </div>

                  <button 
                    @click="addEvent(secIdx, perIdx)" 
                    class="flex items-center bg-background border border-dashed hover:border-primary hover:text-primary rounded-full px-3 py-1 text-xs text-muted-foreground transition-colors"
                  >
                    <Plus class="w-3 h-3 mr-1" /> Evento
                  </button>
                </div>
              </div>

              <div class="mt-2 pl-4">
                <Button variant="secondary" size="sm" @click="addPeriod(secIdx)" class="h-7 text-xs">
                  <Plus class="w-3 h-3 mr-1" /> Añadir Periodo
                </Button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
